import React, { useState, useEffect } from 'react';
import { getClients, deleteClient, bulkDeleteClients, exportClients, addActivity, toggleClientFavorite, getWhatsAppIntegrations, createOrGetConversation } from '../utils/api';
import ClientModal from './ClientModal';
import ClientAvatar from './ClientAvatar';
import ClientProfilePanel from './ClientProfilePanel';
import ActivityModal from './ActivityModal';
import ReminderModal from './ReminderModal';
import DealsModal from './DealsModal';
import { ar, en } from '../utils/translations';
// React Icons - Colorful icons
import { FaPlus, FaTh, FaTable, FaStar, FaWhatsapp, FaFileDownload, FaFileImport } from 'react-icons/fa';

const Clients = ({ language = 'ar', onNavigateToWhatsApp, onOpenPhoneImport, userRole = 'owner' }) => {
  const t = language === 'ar' ? ar : en;
  const isRTL = language === 'ar';
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all'); // 'all', 'prospect', 'customer'
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState('split'); // 'split' = table + profile, 'table' = full table
  const [modalViewMode, setModalViewMode] = useState(false); // true for view-only mode
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [isNarrow, setIsNarrow] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 960 : false));
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  
  // Removed pagination for performance

  // WhatsApp number picker modal
  const [waPickerClient, setWaPickerClient] = useState(null); // client to open conversation for
  const [waIntegrations, setWaIntegrations] = useState(null); // null = not loaded yet
  const [waPickerLoading, setWaPickerLoading] = useState(false);
  const [waPickerError, setWaPickerError] = useState(null);

  useEffect(() => {
    loadClients();
    setSelectedIds(new Set()); // Clear selection when search/filter changes
  }, [searchTerm, showFavoritesOnly, customerTypeFilter]);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 960);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!clients.length) {
      setSelectedProfileId(null);
      return;
    }
    if (!selectedProfileId || !clients.some((c) => c.id === selectedProfileId)) {
      setSelectedProfileId(clients[0].id);
    }
  }, [clients, selectedProfileId]);

  const selectedProfile = clients.find((c) => c.id === selectedProfileId) || null;

  const selectClientProfile = (client) => {
    if (!client) return;
    setSelectedProfileId(client.id);
    if (isNarrow) setMobileProfileOpen(true);
  };

  const loadClients = async () => {
    try {
      console.log('🔍 Loading clients debug:');
      console.log('   Search term:', searchTerm);
      console.log('   Show favorites only:', showFavoritesOnly);
      console.log('   Customer type filter:', customerTypeFilter);
      
      setLoading(true);
      const startTime = Date.now();
      
      const response = await getClients(
        searchTerm, 
        null, 
        showFavoritesOnly ? 'true' : null,
        customerTypeFilter !== 'all' ? customerTypeFilter : null
      );
      
      const loadTime = Date.now() - startTime;
      console.log('✅ Clients API response received:');
      console.log(`   Load time: ${loadTime}ms`);
      console.log('   Response type:', Array.isArray(response) ? 'array' : typeof response);
      console.log('   Response data type:', Array.isArray(response?.data) ? 'array' : typeof response?.data);
      console.log('   Response length:', response?.data?.length || response?.length || 'unknown');
      console.log('   First client:', response?.data?.[0] || response?.[0] || 'none');
      
      // Sort clients by importance: high > medium > low
      const sortedClients = [...response.data].sort((a, b) => {
        const importanceOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        const aImportance = importanceOrder[a.importance] || 0;
        const bImportance = importanceOrder[b.importance] || 0;
        return bImportance - aImportance; // Descending order (highest first)
      });
      setClients(sortedClients);
      console.log('✅ Clients set in state:', sortedClients.length);
    } catch (error) {
      console.error('❌ Error loading clients:', error);
      console.error('   Error details:', error.message);
      console.error('   Error code:', error.code);
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        alert(t.cannotConnect + '\n\n' + t.runCommand);
      } else {
        alert(t.failedToLoad);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get customer type display
  const getCustomerTypeDisplay = (customerType) => {
    const typeMap = {
      'prospect': { label: language === 'ar' ? 'عميل محتمل' : 'Prospect', color: '#3b82f6', bgColor: '#dbeafe' },
      'customer': { label: language === 'ar' ? 'عميل حالي' : 'Customer', color: '#10b981', bgColor: '#d1fae5' }
    };
    return typeMap[customerType] || typeMap['customer'];
  };

  const formatAiBudget = (budget) => {
    if (!budget || (budget.min == null && budget.max == null)) return null;
    const cur = language === 'ar' ? 'ريال' : (budget.currency || 'SAR');
    if (budget.min != null && budget.max != null) return `${Number(budget.min).toLocaleString()} – ${Number(budget.max).toLocaleString()} ${cur}`;
    if (budget.max != null) return `${Number(budget.max).toLocaleString()} ${cur}`;
    if (budget.min != null) return `${Number(budget.min).toLocaleString()} ${cur}`;
    return null;
  };

  const getAiLabel = (key, value) => {
    const dict = {
      intent: {
        buy: { ar: 'شراء', en: 'Buy' },
        rent: { ar: 'إيجار', en: 'Rent' },
        inquiry: { ar: 'استفسار', en: 'Inquiry' },
        unknown: { ar: 'غير محدد', en: 'Unknown' },
      },
      property_type: {
        apartment: { ar: 'شقة', en: 'Apartment' },
        villa: { ar: 'فيلا', en: 'Villa' },
        land: { ar: 'أرض', en: 'Land' },
        office: { ar: 'مكتب', en: 'Office' },
        unknown: { ar: 'غير محدد', en: 'Unknown' },
      },
      level: {
        high: { ar: 'عالي', en: 'High' },
        medium: { ar: 'متوسط', en: 'Medium' },
        low: { ar: 'منخفض', en: 'Low' },
        unknown: { ar: 'غير محدد', en: 'Unknown' },
      },
    };
    if (key === 'intent') return dict.intent[value]?.[language] || value;
    if (key === 'property_type') return dict.property_type[value]?.[language] || value;
    if (key === 'level') return dict.level[value]?.[language] || value;
    return value;
  };

  const AiChip = ({ label, value, tone }) => {
    if (!value) return null;
    const toneColor = tone === 'high' ? '#ea580c' : tone === 'medium' ? '#b45309' : tone === 'low' ? '#64748b' : '#0f172a';
    const toneBorder = tone === 'high' ? '#fed7aa' : tone === 'medium' ? '#fde68a' : tone === 'low' ? '#e2e8f0' : '#e2e8f0';
    const toneBg = tone === 'high' ? '#fff7ed' : tone === 'medium' ? '#fffbeb' : tone === 'low' ? '#f8fafc' : '#f8fafc';
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 8px', borderRadius: '999px', border: `1px solid ${toneBorder}`, background: toneBg }}>
        <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: '10px', color: toneColor, fontWeight: 700 }}>{value}</span>
      </div>
    );
  };

  const ClientAiRow = ({ client }) => {
    const ai = client?.ai_profile;
    if (!ai) return null;
    const budget = formatAiBudget(ai.budget);
    // List triage: keep chips short — skip readiness if priority already shown
    const chips = [
      ai.intent && ai.intent !== 'unknown' ? { label: language === 'ar' ? 'الغرض' : 'Intent', value: getAiLabel('intent', ai.intent) } : null,
      ai.property_type && ai.property_type !== 'unknown' ? { label: language === 'ar' ? 'النوع' : 'Type', value: getAiLabel('property_type', ai.property_type) } : null,
      budget ? { label: language === 'ar' ? 'الميزانية' : 'Budget', value: budget } : null,
      ai.priority && ai.priority !== 'unknown' ? { label: language === 'ar' ? 'الأولوية' : 'Priority', value: getAiLabel('level', ai.priority), tone: ai.priority } : null,
    ].filter(Boolean);
    if (chips.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {chips.map((c) => (
          <AiChip key={`${c.label}-${c.value}`} label={c.label} value={c.value} tone={c.tone} />
        ))}
      </div>
    );
  };

  const handleToggleFavorite = async (client, e) => {
    e.stopPropagation();
    try {
      const newFavoriteStatus = !client.is_favorite;
      await toggleClientFavorite(client.id, newFavoriteStatus);
      // Update local state
      setClients(clients.map(c => 
        c.id === client.id ? { ...c, is_favorite: newFavoriteStatus ? 1 : 0 } : c
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert(t.failedToUpdate || 'Failed to update favorite status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t.areYouSureDelete)) {
      try {
        await deleteClient(id);
        loadClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert(t.failedToDelete);
      }
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setModalViewMode(false);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingClient(null);
    setModalViewMode(false);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingClient(null);
    setModalViewMode(false);
    loadClients();
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(clients.map(c => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      alert(t.selectAtLeastOne);
      return;
    }

    const count = selectedIds.size;
    if (!window.confirm(`${t.areYouSureDeleteMultiple} ${count} ${t.clientsText}`)) {
      return;
    }

    try {
      await bulkDeleteClients(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadClients();
    } catch (error) {
      console.error('Error deleting clients:', error);
      alert(t.failedToDelete);
    }
  };

  // Helper function to clean phone number for calling
  const cleanPhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove all non-digit characters except +
    return phone.replace(/[^\d+]/g, '');
  };

  // Handle phone call
  const handleCall = (phone) => {
    if (!phone) {
      alert(t.noPhoneNumber);
      return;
    }
    const cleanedPhone = cleanPhoneNumber(phone);
    window.location.href = `tel:${cleanedPhone}`;
  };

  // Fallback: open wa.me externally (used when no integration is available)
  const openWaMeLink = (phone) => {
    const cleanedPhone = cleanPhoneNumber(phone);
    const whatsappPhone = cleanedPhone.startsWith('+') ? cleanedPhone.substring(1) : cleanedPhone;
    window.open(`https://wa.me/${whatsappPhone}`, '_blank');
  };

  // Handle WhatsApp chat — prefers the integrated inbox when integrations exist
  const handleWhatsApp = async (phone, client) => {
    if (!phone) {
      alert(t.noWhatsApp || 'No WhatsApp number');
      return;
    }

    // Log activity
    const targetClient = client || clients.find(c => c.phone === phone || c.whatsapp === phone);
    if (targetClient) {
      addActivity({
        contact_type: 'client',
        contact_id: targetClient.id,
        activity_type: 'whatsapp',
        title: t.whatsapp,
        description: `${t.whatsapp} ${phone}`
      }).catch(err => console.error('Error logging activity:', err));
    }

    // Load integrations once and cache result for the session.
    // API returns { integrations: [...] } (same shape as WhatsAppSettings / WhatsAppInbox).
    let integrations = waIntegrations;
    if (integrations === null) {
      try {
        const res = await getWhatsAppIntegrations();
        const payload = res?.data;
        integrations = Array.isArray(payload?.integrations)
          ? payload.integrations
          : Array.isArray(payload)
            ? payload
            : [];
        setWaIntegrations(integrations);
      } catch (_) {
        integrations = [];
        setWaIntegrations([]);
      }
    }

    if (integrations.length === 0) {
      // No integrated numbers — fall back to wa.me
      openWaMeLink(phone);
      return;
    }

    if (integrations.length === 1) {
      // Exactly one integration — go straight to inbox
      await openInboxConversation(phone, integrations[0].phone_number_id);
      return;
    }

    // Multiple integrations — let the user pick
    setWaPickerClient({ phone, client: targetClient });
    setWaPickerError(null);
  };

  // Open (or create) a conversation in the inbox for the given phone + integration
  const openInboxConversation = async (phone, phoneNumberId) => {
    setWaPickerLoading(true);
    setWaPickerError(null);
    try {
      // Digits-only + strip doubled country code (e.g. +966966568019465 → 966568019465).
      let normalizedPhone = String(phone || '').replace(/\D/g, '');
      if (normalizedPhone.startsWith('966966') && normalizedPhone.length >= 15) {
        normalizedPhone = normalizedPhone.slice(3);
      }
      if (!normalizedPhone) {
        setWaPickerError(language === 'ar' ? 'رقم غير صالح' : 'Invalid phone number');
        return;
      }
      const res = await createOrGetConversation(normalizedPhone, phoneNumberId);
      const conv = res.data;
      setWaPickerClient(null);
      // Navigate to the WhatsApp inbox with the conversation pre-selected
      if (typeof onNavigateToWhatsApp === 'function') {
        onNavigateToWhatsApp(conv);
      } else {
        // Fallback if parent hasn't wired the callback yet
        window.location.hash = '#whatsapp';
      }
    } catch (err) {
      setWaPickerError(
        language === 'ar'
          ? 'تعذّر فتح المحادثة. يُرجى المحاولة مرة أخرى.'
          : 'Failed to open conversation. Please try again.'
      );
    } finally {
      setWaPickerLoading(false);
    }
  };

  // Handle email
  const handleEmail = (email) => {
    if (!email) {
      alert(t.noEmail || 'No email address');
      return;
    }
    window.location.href = `mailto:${email}`;
    // Log activity
    const client = clients.find(c => c.email === email);
    if (client) {
      addActivity({
        contact_type: 'client',
        contact_id: client.id,
        activity_type: 'email',
        title: t.email,
        description: `${t.email} ${email}`
      }).catch(err => console.error('Error logging activity:', err));
    }
  };

  // Handle view client — prefer split/profile panel; form modal stays for edit
  const handleView = (client) => {
    selectClientProfile(client);
  };

  // Handle view activities
  const handleViewActivities = (client) => {
    setSelectedClient(client);
    setShowActivityModal(true);
  };

  // Handle view reminders
  const handleViewReminders = (client) => {
    setSelectedClient(client);
    setShowReminderModal(true);
  };

  // Handle view deals
  const handleViewDeals = (client) => {
    setSelectedClient(client);
    setShowDealsModal(true);
  };

  return (
    <div>
      <div className="card">
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px', fontWeight: '600', color: '#000000' }}>
            {t.searchClients}
          </h2>
          <div className="search-bar">
            <input
              type="text"
              placeholder={t.searchClientsPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ fontSize: '1.1rem', padding: '14px 18px' }}
            />
          {selectedIds.size > 0 && (
            <button 
              className="btn btn-secondary" 
              onClick={handleBulkDelete}
              style={{ 
                opacity: 0.8,
                background: '#faf5f0',
                border: '1px solid #e0e0e0'
              }}
            >
              🗑️ {t.deleteSelected} ({selectedIds.size})
            </button>
          )}
          <div className="search-bar-buttons">
            <select
              value={customerTypeFilter}
              onChange={(e) => setCustomerTypeFilter(e.target.value)}
              style={{
                padding: '8px 14px',
                background: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                color: '#000000',
                fontSize: '0.9rem',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
              <option value="prospect">{language === 'ar' ? 'عميل محتمل' : 'Prospect'}</option>
              <option value="customer">{language === 'ar' ? 'عميل حالي' : 'Customer'}</option>
            </select>
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              style={{
                padding: '8px 14px',
                background: showFavoritesOnly ? '#fff4e6' : '#ffffff',
                border: '1px solid',
                borderColor: showFavoritesOnly ? '#ffd4a3' : '#e0e0e0',
                borderRadius: '6px',
                color: '#000000',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: showFavoritesOnly ? '500' : '400',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!showFavoritesOnly) {
                  e.currentTarget.style.background = '#faf5f0';
                  e.currentTarget.style.borderColor = '#ffd4a3';
                }
              }}
              onMouseLeave={(e) => {
                if (!showFavoritesOnly) {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }
              }}
              title={showFavoritesOnly ? t.showAll : t.showFavorites}
            >
              <FaStar style={{ color: showFavoritesOnly ? '#f97316' : '#ccc', fontSize: '16px' }} /> {t.favorites || 'Favorites'}
            </button>
            <div className="view-mode-toggle" style={{ display: 'flex', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
              <button
                onClick={() => setViewMode('split')}
                style={{ 
                  borderRadius: 0, 
                  border: 'none',
                  borderRight: '1px solid #e0e0e0',
                  padding: '8px 14px',
                  background: viewMode === 'split' ? '#fff4e6' : '#ffffff',
                  color: '#000000',
                  fontSize: '0.9rem',
                  fontWeight: viewMode === 'split' ? '500' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (viewMode !== 'split') {
                    e.currentTarget.style.background = '#faf5f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode !== 'split') {
                    e.currentTarget.style.background = '#ffffff';
                  }
                }}
              >
                <FaTh style={{ fontSize: '14px', color: '#f97316' }} /> {language === 'ar' ? 'جدول + ملف' : 'Table + profile'}
              </button>
              <button
                onClick={() => setViewMode('table')}
                style={{ 
                  borderRadius: 0, 
                  border: 'none',
                  padding: '8px 14px',
                  background: viewMode === 'table' ? '#fff4e6' : '#ffffff',
                  color: '#000000',
                  fontSize: '0.9rem',
                  fontWeight: viewMode === 'table' ? '500' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (viewMode !== 'table') {
                    e.currentTarget.style.background = '#faf5f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (viewMode !== 'table') {
                    e.currentTarget.style.background = '#ffffff';
                  }
                }}
              >
                <FaTable style={{ fontSize: '14px', color: '#f97316' }} /> {t.table}
              </button>
            </div>
            <button 
              onClick={handleAdd}
              style={{
                padding: '8px 16px',
                fontSize: '0.9rem',
                fontWeight: '500',
                background: '#ffffff',
                color: '#000000',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#faf5f0';
                e.currentTarget.style.borderColor = '#ffd4a3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <FaPlus style={{ fontSize: '14px', color: '#f97316' }} /> {t.addClient}
            </button>
            <button 
              className="export-btn"
              onClick={exportClients}
              style={{
                padding: '8px 16px',
                fontSize: '0.9rem',
                fontWeight: '500',
                background: '#ffffff',
                color: '#000000',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#faf5f0';
                e.currentTarget.style.borderColor = '#ffd4a3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <FaFileDownload style={{ fontSize: '14px', color: '#f97316' }} /> {t.exportClients}
            </button>
            {typeof onOpenPhoneImport === 'function' && (
              <button
                type="button"
                onClick={onOpenPhoneImport}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  background: '#ffffff',
                  color: '#000000',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#faf5f0';
                  e.currentTarget.style.borderColor = '#ffd4a3';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
              >
                <FaFileImport style={{ fontSize: '14px', color: '#c2410c' }} /> {t.importContacts}
              </button>
            )}
          </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <p>{t.loadingClients}</p>
        </div>
      ) : clients.length === 0 ? (
        <div className="card empty-state">
          <h3>{t.noClients}</h3>
          <p>{t.noClientsDesc}</p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="card" style={{ overflow: 'visible' }}>
          <div className="table-container" style={{ overflowX: 'auto', width: '100%', display: 'block' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#faf5f0', borderBottom: '2px solid #ffb84d' }}>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === clients.length && clients.length > 0}
                      onChange={handleSelectAll}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>{t.name}</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                    <FaStar style={{ fontSize: '16px', color: '#f97316' }} />
                  </th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>{t.phone}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>{t.whatsapp}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>{t.email}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>{t.address}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>{t.status}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr
                    key={client.id}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: selectedIds.has(client.id) ? '#ffe8cc' : 'white'
                    }}
                  >
                    <td style={{ padding: '12px', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(client.id)}
                        onChange={() => handleSelectOne(client.id)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0', fontWeight: '500' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ClientAvatar name={client.name} phone={client.phone} seed={client.id} size={36} radius={10} />
                        <div>
                          <div>{client.name}</div>
                          <ClientAiRow client={client} />
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                      <button
                        onClick={(e) => handleToggleFavorite(client, e)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                          padding: '4px',
                          color: client.is_favorite ? '#ffb84d' : '#ccc',
                          transition: 'all 0.2s ease'
                        }}
                        title={client.is_favorite ? t.removeFromFavorites : t.addToFavorites}
                      >
                        <FaStar style={{ color: client.is_favorite ? '#f97316' : '#ccc', fontSize: '18px' }} />
                      </button>
                    </td>
                    <td style={{ padding: '12px', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                      {client.phone || '-'}
                    </td>
                    <td style={{ padding: '12px', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                      {client.whatsapp || '-'}
                    </td>
                    <td style={{ padding: '12px', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                      {client.email || '-'}
                    </td>
                    <td style={{ padding: '12px', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {client.address || '-'}
                    </td>
                    <td style={{ padding: '12px', borderRight: language === 'ar' ? '1px solid #e0e0e0' : 'none', borderLeft: language === 'ar' ? 'none' : '1px solid #e0e0e0' }}>
                      <span className={`status-badge status-${client.status || 'active'}`}>
                        {client.status === 'active' ? t.active : t.inactive}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleEdit(client)}
                        >
                          {t.edit}
                        </button>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleDelete(client.id)}
                          style={{ 
                            padding: '6px 10px',
                            fontSize: '0.85rem',
                            opacity: 0.7,
                            background: '#faf5f0',
                            border: '1px solid #e0e0e0'
                          }}
                          title={t.delete}
                          onMouseEnter={(e) => e.target.style.opacity = '1'}
                          onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {clients.length > 0 && (
            <div style={{ 
              padding: '10px 20px', 
              background: '#faf5f0', 
              borderRadius: '8px', 
              marginTop: '15px',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '500', color: '#000000' }}>
                {selectedIds.size} من {clients.length} {t.selected}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isNarrow ? '1fr' : 'minmax(360px, 1fr) minmax(380px, 1.15fr)',
            gap: 16,
            alignItems: 'stretch',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <div
            style={{
              minWidth: 0,
              border: '1px solid #ffe8cc',
              borderRadius: 12,
              background: '#ffffff',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '10px 14px',
                background: '#faf5f0',
                borderBottom: '1px solid #ffe8cc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={selectedIds.size === clients.length && clients.length > 0}
                  onChange={handleSelectAll}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600, fontSize: 13, color: '#000' }}>
                  {t.selectAll} ({selectedIds.size})
                </span>
              </label>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {clients.length} {language === 'ar' ? 'عميل' : 'clients'}
              </span>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: isRTL ? 'right' : 'left', background: '#faf8f3', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #ffe8cc', width: 36 }} />
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #ffe8cc', width: 44 }} />
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #ffe8cc', fontWeight: 700 }}>{t.name}</th>
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #ffe8cc', fontWeight: 700 }}>
                      {language === 'ar' ? 'النوع' : 'Type'}
                    </th>
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #ffe8cc', fontWeight: 700 }}>
                      {language === 'ar' ? 'الغرض' : 'Intent'}
                    </th>
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid #ffe8cc', fontWeight: 700 }}>
                      {language === 'ar' ? 'الأولوية' : 'Priority'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => {
                    const active = selectedProfileId === client.id;
                    const ai = client.ai_profile || {};
                    const intentLabel =
                      ai.intent && ai.intent !== 'unknown' ? getAiLabel('intent', ai.intent) : '—';
                    const priorityLabel =
                      ai.priority && ai.priority !== 'unknown' ? getAiLabel('level', ai.priority) : '—';
                    const typeDisp = getCustomerTypeDisplay(client.customer_type);
                    return (
                      <tr
                        key={client.id}
                        onClick={() => selectClientProfile(client)}
                        style={{
                          cursor: 'pointer',
                          background: active ? '#fffbeb' : selectedIds.has(client.id) ? '#fff7ed' : '#ffffff',
                          outline: active ? '1px solid #ea580c' : 'none',
                          outlineOffset: -1,
                        }}
                      >
                        <td
                          style={{ padding: '8px 10px', borderBottom: '1px solid #f0ebe3' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(client.id)}
                            onChange={() => handleSelectOne(client.id)}
                            style={{ width: 15, height: 15, cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '8px 6px', borderBottom: '1px solid #f0ebe3' }}>
                          <ClientAvatar
                            name={client.name}
                            phone={client.phone}
                            seed={client.id}
                            size={32}
                            radius={9}
                          />
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0ebe3', fontWeight: active ? 700 : 500 }}>
                          {(() => {
                            const nameStr = String(client.name || '').trim();
                            const phoneDigits = String(client.phone || '').replace(/\D/g, '');
                            const nameDigits = nameStr.replace(/\D/g, '');
                            const nameIsPhone =
                              !nameStr ||
                              (nameDigits.length >= 7 && nameDigits === phoneDigits);
                            const title = nameIsPhone
                              ? (client.phone || `#${client.id}`)
                              : nameStr;
                            return (
                              <>
                                <div>{title}</div>
                                {!nameIsPhone && client.phone ? (
                                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{client.phone}</div>
                                ) : null}
                              </>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0ebe3' }}>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: typeDisp.color,
                              background: typeDisp.bgColor,
                              borderRadius: 999,
                              padding: '2px 8px',
                            }}
                          >
                            {typeDisp.label}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0ebe3' }}>{intentLabel}</td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0ebe3' }}>
                          {priorityLabel !== '—' ? (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: ai.priority === 'high' ? '#ea580c' : '#b45309',
                                background: ai.priority === 'high' ? '#fff7ed' : '#fffbeb',
                                border: '1px solid #fed7aa',
                                borderRadius: 999,
                                padding: '2px 8px',
                              }}
                            >
                              {priorityLabel}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {!isNarrow && (
            <div style={{ position: 'sticky', top: 12, alignSelf: 'start', maxHeight: 'calc(100vh - 120px)', overflow: 'hidden' }}>
              <ClientProfilePanel
                client={selectedProfile}
                language={language}
                userRole={userRole}
                onEdit={handleEdit}
                onToggleFavorite={handleToggleFavorite}
                onCall={(c) => {
                  handleCall(c.phone);
                  addActivity({
                    contact_type: 'client',
                    contact_id: c.id,
                    activity_type: 'call',
                    title: t.call,
                    description: `${t.call} ${c.phone}`,
                  }).catch(() => {});
                }}
                onWhatsApp={(c) => handleWhatsApp(c.whatsapp || c.phone, c)}
                onOpenDeals={handleViewDeals}
                onOpenActivities={handleViewActivities}
                onOpenReminders={handleViewReminders}
              />
            </div>
          )}

          {isNarrow && mobileProfileOpen && selectedProfile && (
            <div
              className="modal-overlay"
              onClick={() => setMobileProfileOpen(false)}
              style={{ zIndex: 1200 }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 'min(520px, 100%)',
                  maxHeight: '92vh',
                  margin: isRTL ? '0 auto 0 0' : '0 0 0 auto',
                  height: '100%',
                  maxWidth: 520,
                }}
              >
                <ClientProfilePanel
                  client={selectedProfile}
                  language={language}
                  userRole={userRole}
                  onClose={() => setMobileProfileOpen(false)}
                  onToggleFavorite={handleToggleFavorite}
                  onEdit={(c) => {
                    setMobileProfileOpen(false);
                    handleEdit(c);
                  }}
                  onCall={(c) => {
                    handleCall(c.phone);
                    addActivity({
                      contact_type: 'client',
                      contact_id: c.id,
                      activity_type: 'call',
                      title: t.call,
                      description: `${t.call} ${c.phone}`,
                    }).catch(() => {});
                  }}
                  onWhatsApp={(c) => handleWhatsApp(c.whatsapp || c.phone, c)}
                  onOpenDeals={(c) => {
                    setMobileProfileOpen(false);
                    handleViewDeals(c);
                  }}
                  onOpenActivities={(c) => {
                    setMobileProfileOpen(false);
                    handleViewActivities(c);
                  }}
                  onOpenReminders={(c) => {
                    setMobileProfileOpen(false);
                    handleViewReminders(c);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <ClientModal
          client={editingClient}
          onClose={handleModalClose}
          language={language}
          viewMode={modalViewMode}
          userRole={userRole}
        />
      )}
      
      {showActivityModal && selectedClient && (
        <ActivityModal
          contactType="client"
          contactId={selectedClient.id}
          contactName={selectedClient.name}
          onClose={() => {
            setShowActivityModal(false);
            setSelectedClient(null);
          }}
          language={language}
        />
      )}
      
      {showReminderModal && selectedClient && (
        <ReminderModal
          contactType="client"
          contactId={selectedClient.id}
          contactName={selectedClient.name}
          onClose={() => {
            setShowReminderModal(false);
            setSelectedClient(null);
          }}
          language={language}
        />
      )}
      
      {showDealsModal && selectedClient && (
        <DealsModal
          contactType="client"
          contactId={selectedClient.id}
          contactName={selectedClient.name}
          onClose={() => {
            setShowDealsModal(false);
            setSelectedClient(null);
          }}
          language={language}
        />
      )}

      {/* WhatsApp number picker modal — shown when org has multiple integrated numbers */}
      {waPickerClient && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => { if (!waPickerLoading) setWaPickerClient(null); }}
        >
          <div
            style={{
              background: '#fff', borderRadius: '16px', padding: '28px 24px',
              maxWidth: 380, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
              direction: language === 'ar' ? 'rtl' : 'ltr'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
              {language === 'ar' ? 'اختر رقم واتساب' : 'Choose WhatsApp Number'}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '18px' }}>
              {language === 'ar'
                ? `فتح محادثة مع ${waPickerClient.client?.name || waPickerClient.phone}`
                : `Open conversation with ${waPickerClient.client?.name || waPickerClient.phone}`}
            </div>
            {waPickerError && (
              <div style={{ fontSize: '13px', color: '#dc2626', background: '#fef2f2', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px' }}>
                {waPickerError}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(waIntegrations || []).map(intg => (
                <button
                  key={intg.phone_number_id || intg.id}
                  disabled={waPickerLoading}
                  onClick={() => openInboxConversation(waPickerClient.phone, intg.phone_number_id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', borderRadius: '10px',
                    border: '1.5px solid #d1fae5', background: '#f0fdf4',
                    cursor: waPickerLoading ? 'not-allowed' : 'pointer',
                    textAlign: language === 'ar' ? 'right' : 'left',
                    opacity: waPickerLoading ? 0.6 : 1,
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => { if (!waPickerLoading) e.currentTarget.style.background = '#dcfce7'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                >
                  <FaWhatsapp style={{ color: '#16a34a', fontSize: '20px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                      {intg.display_phone_number || intg.business_display_name || intg.phone_number_id}
                    </div>
                    {intg.business_display_name && intg.display_phone_number && (
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{intg.business_display_name}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {waPickerLoading && (
              <div style={{ textAlign: 'center', marginTop: '14px', fontSize: '13px', color: '#64748b' }}>
                {language === 'ar' ? 'جارٍ الفتح…' : 'Opening…'}
              </div>
            )}
            <button
              onClick={() => setWaPickerClient(null)}
              disabled={waPickerLoading}
              style={{
                marginTop: '18px', width: '100%', padding: '10px',
                background: 'transparent', border: '1px solid #e2e8f0',
                borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b'
              }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;

